// 2.4 : boîte de réception WhatsApp. Les clients écrivent au numéro WhatsApp du bureau ; les agents lisent et
// répondent ici. Réponse écrite possible seulement dans les 24 h qui suivent le dernier message du client
// (règle de Meta) ; après, il faut l'appeler depuis le fixe.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, MessageCircle, Send, Loader2, RefreshCw, Clock, UserRound, Phone } from "lucide-react";
import { API, FOCUS, entetes, estAnnulation, estNonAutorise, messageErreur, nomComplet } from "./outils";

const TEL_BUREAU = "039 80 81 41";

// « +213 661 11 22 33 » pour un numéro 213661112233
export function numeroLisible(n) {
  const s = String(n || "");
  if (s.startsWith("213") && s.length === 12) return `+213 ${s.slice(3, 6)} ${s.slice(6, 8)} ${s.slice(8, 10)} ${s.slice(10)}`;
  return `+${s}`;
}

function heureCourte(iso, maintenant = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Algiers" });
  const jour = (x) => x.toLocaleDateString("fr-FR", { timeZone: "Africa/Algiers" });
  if (jour(d) === jour(maintenant)) return hh;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", timeZone: "Africa/Algiers" })} ${hh}`;
}

function titreClient(c, numero) {
  return c ? nomComplet(c) : numeroLisible(numero);
}

function EnTete({ titre, sousTitre, onRetour, droite }) {
  return (
    <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] flex items-center gap-2">
      <button
        type="button"
        onClick={onRetour}
        aria-label="Retour"
        className={`w-11 h-11 -ml-2.5 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
      >
        <ChevronLeft aria-hidden="true" className="w-[22px] h-[22px]" />
      </button>
      <div className="flex flex-col flex-1 min-w-0">
        <h1 className="m-0 text-white font-bold text-base leading-tight truncate">{titre}</h1>
        {sousTitre ? <span className="text-[#94A3B8] text-xs truncate">{sousTitre}</span> : null}
      </div>
      {droite}
    </header>
  );
}

// Charge une adresse de l'API, puis la relit toutes les `periode` ms (et au retour au premier plan) tant que
// l'écran est visible (`actif`). Le premier chargement est noté au journal ; les relectures (?auto=1) ne le sont
// pas et ne prolongent pas la session de l'agent.
// 2.5.5 : marquerLu (conversation) — une relecture ne marque les messages « lus » que si la fenêtre est AU PREMIER
// PLAN (quelqu'un la regarde) ; une conversation oubliée en arrière-plan ne « mange » plus la pastille du logiciel.
function useChargement(code, url, periode, onLogout, actif = true, marquerLu = false) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState("");
  const onLogoutRef = useRef(onLogout);
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);
  const ctrlRef = useRef(null);
  const premier = useRef(true);
  const charger = useCallback(() => {
    if (ctrlRef.current) ctrlRef.current.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const params = premier.current ? {} : { auto: "1" };
    if (marquerLu && !premier.current) params.lu = document.hasFocus() ? "1" : "0";
    premier.current = false;
    axios
      .get(url, { headers: entetes(code), params, signal: ctrl.signal, timeout: 20000 })
      .then(({ data }) => {
        setDonnees(data);
        setErreur("");
      })
      .catch((e) => {
        if (estAnnulation(e)) return;
        if (estNonAutorise(e)) {
          onLogoutRef.current();
          return;
        }
        setErreur(messageErreur(e));
      });
  }, [code, url, marquerLu]);
  useEffect(() => {
    if (!actif) return undefined;   // écran couvert (fiche ouverte par-dessus) : aucune relecture
    const visible = () => {
      if (document.visibilityState !== "hidden") charger();
    };
    visible();
    const minuteur = window.setInterval(visible, periode);
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("focus", visible);   // retour sur la fenêtre : relecture (et messages marqués lus)
    return () => {
      window.clearInterval(minuteur);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("focus", visible);
      if (ctrlRef.current) ctrlRef.current.abort();
    };
  }, [charger, periode, actif]);
  return { donnees, erreur, charger };
}

export default function EcranMessages({ code, actif, onRetour, onOuvrirConversation, onLogout }) {
  const { donnees, erreur, charger } = useChargement(code, `${API}/mobile/whatsapp/conversations`, 30000, onLogout, actif);
  const liste = (donnees && donnees.conversations) || [];
  return (
    <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]" data-testid="ecran-messages">
      <EnTete
        titre="Messages WhatsApp"
        sousTitre="Messages des clients au numéro du bureau"
        onRetour={onRetour}
        droite={
          <button
            type="button"
            onClick={charger}
            aria-label="Actualiser"
            className={`w-11 h-11 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
          >
            <RefreshCw aria-hidden="true" className="w-5 h-5" />
          </button>
        }
      />
      {donnees && !donnees.actif ? (
        <p role="status" className="m-0 mx-3 mt-3 text-[13px] text-[#9A3412] bg-[#FFF7ED] border border-[#FDBA74] rounded-xl px-3 py-2.5">
          WhatsApp est coupé sur le serveur : les messages arrivent, mais les réponses ne partent pas.
        </p>
      ) : null}
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 flex flex-col gap-2">
        {!donnees && !erreur ? (
          <p role="status" className="m-0 p-8 text-center text-sm text-[#475569] flex items-center justify-center gap-2">
            <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
          </p>
        ) : null}
        {erreur ? (
          <p role="alert" className="m-0 p-4 text-center text-sm text-[#B91C1C]">
            {erreur}
          </p>
        ) : null}
        {donnees && !liste.length ? (
          <div className="m-auto p-8 text-center text-[#475569] flex flex-col items-center gap-2">
            <MessageCircle aria-hidden="true" className="w-9 h-9 text-[#94A3B8]" />
            <span className="text-sm">Aucun message de client pour le moment.</span>
          </div>
        ) : null}
        {liste.map((c) => (
          <button
            key={c.numero}
            type="button"
            onClick={() => onOuvrirConversation(c.numero)}
            data-testid="conversation"
            className={`w-full text-left bg-white border ${c.non_lus ? "border-[#14B8A6]" : "border-[#E2E8F0]"} rounded-[14px] p-3 flex items-start gap-3 ${FOCUS}`}
          >
            <span
              aria-hidden="true"
              className={`w-10 h-10 flex-none rounded-full flex items-center justify-center ${c.client ? "bg-[#CCFBF1] text-[#0F766E]" : "bg-[#F1F5F9] text-[#64748B]"}`}
            >
              <UserRound className="w-5 h-5" />
            </span>
            <span className="flex flex-col flex-1 min-w-0 gap-0.5">
              <span className="flex items-center gap-2">
                <span className={`flex-1 min-w-0 truncate text-[15px] ${c.non_lus ? "font-extrabold" : "font-semibold"}`}>
                  {titreClient(c.client, c.numero)}
                </span>
                <span className="flex-none text-xs text-[#64748B]">{heureCourte(c.dernier_quand)}</span>
              </span>
              <span className="text-xs text-[#64748B] truncate">
                {c.client ? `${c.client.ref || "sans réf."} · ${numeroLisible(c.numero)}` : "Numéro sans fiche"}
              </span>
              <span className="flex items-center gap-2">
                <span className={`flex-1 min-w-0 truncate text-[13px] ${c.non_lus ? "text-[#0F172A]" : "text-[#475569]"}`}>
                  {c.dernier_sens === "out" ? "Vous : " : ""}
                  {c.dernier_texte}
                </span>
                {c.non_lus ? (
                  <span className="flex-none min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#0F766E] text-white text-xs font-bold flex items-center justify-center">
                    {c.non_lus}
                  </span>
                ) : null}
              </span>
              {!c.fenetre ? (
                <span className="flex items-center gap-1 text-[11px] text-[#9A3412]">
                  <Clock aria-hidden="true" className="w-3 h-3" /> Plus de 24 h : à rappeler
                </span>
              ) : null}
            </span>
          </button>
        ))}
      </main>
    </div>
  );
}

export function EcranConversation({ code, numero, actif, onRetour, onOuvrirFiche, onLogout }) {
  const url = `${API}/mobile/whatsapp/conversations/${encodeURIComponent(numero)}`;
  const { donnees, erreur, charger } = useChargement(code, url, 15000, onLogout, actif, true);
  const [texte, setTexte] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState("");
  const [confirmer, setConfirmer] = useState(false);   // 2.6.8 : réactivation après « Ne plus recevoir »
  const [reactivation, setReactivation] = useState(false);
  const [erreurReactivation, setErreurReactivation] = useState("");
  const finRef = useRef(null);
  const nb = donnees ? donnees.messages.length : 0;
  useEffect(() => {
    if (finRef.current) finRef.current.scrollIntoView({ block: "end" });
  }, [nb]);

  const client = donnees && donnees.client;
  const fenetre = !!(donnees && donnees.fenetre);
  const serveurActif = !!(donnees && donnees.actif);

  const reactiver = () => {
    if (reactivation) return;
    setReactivation(true);
    setErreurReactivation("");
    axios
      .post(`${url}/reactiver`, {}, { headers: entetes(code), timeout: 30000 })
      .then(() => {
        setConfirmer(false);
        charger();
      })
      .catch((err) => {
        if (estNonAutorise(err)) {
          onLogout();
          return;
        }
        setErreurReactivation(messageErreur(err, "Réactivation impossible. Réessayez."));
      })
      .finally(() => setReactivation(false));
  };

  const envoyer = (e) => {
    e.preventDefault();
    const t = texte.trim();
    if (!t || envoi) return;
    setEnvoi(true);
    setErreurEnvoi("");
    axios
      .post(`${url}/repondre`, { texte: t }, { headers: entetes(code), timeout: 60000 })
      .then(({ data }) => {
        setTexte("");
        if (data && data.essai) setErreurEnvoi("Mode essai : message noté mais pas envoyé (numéro hors de la liste d'essai).");
        charger();
      })
      .catch((err) => {
        if (estNonAutorise(err)) {
          onLogout();
          return;
        }
        setErreurEnvoi(messageErreur(err, "Envoi impossible. Réessayez."));
      })
      .finally(() => setEnvoi(false));
  };

  return (
    <div className="ecran flex flex-col bg-[#EFEAE2] text-[#0F172A]" data-testid="ecran-conversation">
      <EnTete
        titre={titreClient(client, numero)}
        sousTitre={client ? `${client.ref || "sans réf."} · ${numeroLisible(numero)}` : `${numeroLisible(numero)} · numéro sans fiche`}
        onRetour={onRetour}
        droite={
          <>
            <a
              href={`tel:+${numero}`}
              aria-label="Appeler le client"
              className={`w-11 h-11 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
            >
              <Phone aria-hidden="true" className="w-5 h-5" />
            </a>
            {client ? (
              <button
                type="button"
                onClick={() => onOuvrirFiche(null, client.id)}
                className={`h-9 px-3 flex-none rounded-lg bg-[#0F766E] text-white text-xs font-bold ${FOCUS}`}
              >
                Fiche
              </button>
            ) : null}
          </>
        }
      />
      {donnees && donnees.refus ? (
        <section
          aria-label="Messages WhatsApp refusés"
          data-testid="bandeau-refus"
          className="flex-none bg-[#FFF7ED] border-b border-[#FDBA74] px-4 py-2.5 text-[#9A3412]"
        >
          {!confirmer ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="m-0 flex-1 min-w-[220px] text-[13px] font-semibold">
                Ce client a demandé à ne plus recevoir de messages WhatsApp : aucun message automatique ne lui est envoyé.
              </p>
              <button
                type="button"
                data-testid="reactiver-whatsapp"
                onClick={() => {
                  setErreurReactivation("");
                  setConfirmer(true);
                }}
                className={`h-9 px-3 flex-none rounded-lg bg-[#C2410C] text-white text-xs font-bold ${FOCUS}`}
              >
                Réactiver WhatsApp
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2" role="group" aria-label="Confirmer la réactivation">
              <p className="m-0 flex-1 min-w-[220px] text-[13px] font-semibold">
                Le client a-t-il demandé à recevoir de nouveau les messages WhatsApp ?
              </p>
              <div className="flex gap-2 flex-none">
                <button
                  type="button"
                  onClick={() => setConfirmer(false)}
                  disabled={reactivation}
                  className={`h-9 px-3 rounded-lg border border-[#FDBA74] bg-white text-[#9A3412] text-xs font-bold ${FOCUS}`}
                >
                  Non
                </button>
                <button
                  type="button"
                  data-testid="reactiver-oui"
                  onClick={reactiver}
                  disabled={reactivation}
                  className={`h-9 px-3 rounded-lg bg-[#0F766E] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-60 ${FOCUS}`}
                >
                  {reactivation ? <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> : null}
                  Oui, réactiver
                </button>
              </div>
            </div>
          )}
          {erreurReactivation ? (
            <p role="alert" className="m-0 mt-1.5 text-[13px] text-[#B91C1C]">
              {erreurReactivation}
            </p>
          ) : null}
        </section>
      ) : null}
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 flex flex-col gap-1.5">
        {!donnees && !erreur ? (
          <p role="status" className="m-0 p-8 text-center text-sm text-[#475569] flex items-center justify-center gap-2">
            <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
          </p>
        ) : null}
        {erreur ? (
          <p role="alert" className="m-0 p-4 text-center text-sm text-[#B91C1C]">
            {erreur}
          </p>
        ) : null}
        {donnees
          ? donnees.messages.map((m) => {
              const sortant = m.sens === "out";
              const auto = sortant && (m.texte.startsWith("[Réponse automatique") || m.genre === "note");
              return (
                <div key={m.id} className={`flex ${sortant ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm ${
                      auto ? "bg-[#F1F5F9] text-[#475569] italic" : sortant ? "bg-[#D9FDD3]" : "bg-white"
                    }`}
                  >
                    <p className="m-0 text-[15px] whitespace-pre-wrap break-words">{m.texte}</p>
                    <p className="m-0 mt-0.5 text-[11px] text-[#64748B] text-right">
                      {sortant && m.agent ? `${m.agent} · ` : ""}
                      {heureCourte(m.quand)}
                      {m.genre === "echec" ? " · non envoyé" : m.genre === "essai" ? " · essai (non envoyé)" : ""}
                    </p>
                  </div>
                </div>
              );
            })
          : null}
        <div ref={finRef} />
      </main>
      <footer className="flex-none bg-white border-t border-[#E2E8F0] p-2 pb-[max(8px,env(safe-area-inset-bottom))]">
        {erreurEnvoi ? (
          <p role="alert" className="m-0 mb-1.5 px-1 text-[13px] text-[#B91C1C]">
            {erreurEnvoi}
          </p>
        ) : null}
        {donnees && (!fenetre || !serveurActif) ? (
          <p role="status" className="m-0 p-2 text-[13px] text-[#9A3412] bg-[#FFF7ED] rounded-lg">
            {!serveurActif
              ? "WhatsApp est coupé sur le serveur : réponse impossible pour le moment."
              : `Plus de 24 h depuis le dernier message du client : WhatsApp n'autorise plus de réponse écrite. Appelez-le depuis le ${TEL_BUREAU}.`}
          </p>
        ) : (
          <form onSubmit={envoyer} className="flex items-end gap-2">
            <label htmlFor="reponse-wa" className="sr-only">
              Votre réponse
            </label>
            <textarea
              id="reponse-wa"
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              rows={1}
              maxLength={1000}
              placeholder="Votre réponse…"
              className="flex-1 min-h-[44px] max-h-32 resize-none rounded-xl border border-[#CBD5E1] px-3 py-2.5 text-[15px] outline-none focus:border-[#14B8A6]"
            />
            <button
              type="submit"
              disabled={!texte.trim() || envoi || !donnees}
              aria-label="Envoyer"
              className={`w-11 h-11 flex-none rounded-full bg-[#0F766E] text-white flex items-center justify-center disabled:opacity-40 ${FOCUS}`}
            >
              {envoi ? <Loader2 aria-hidden="true" className="w-5 h-5 animate-spin" /> : <Send aria-hidden="true" className="w-5 h-5" />}
            </button>
          </form>
        )}
        <p className="m-0 mt-1 px-1 text-[11px] text-[#64748B]">Votre prénom est ajouté à la fin du message.</p>
      </footer>
    </div>
  );
}
